import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setData } from "../../_filtering/state/dataSlice";
import { useGetDataMutation } from "../../_filtering/api/dataApiSlice";
import { useGetOrganisationInfoMutation } from "../../_features/accountSettings/api/organisationApiSlice";
import { setDisplayTimeZone } from "../../_utils/dateFormatter";

/**
 * Bootstraps app-wide reference data once per layout mount. Shared by
 * AppLayout and SettingsLayout instead of each duplicating the same effect.
 *
 * Also applies the organisation's time zone to date display (once it has chosen one) and returns
 * it, so a layout can re-key its routed content when it arrives and every date re-renders in it.
 */
let referenceRequested = false;

/** Forgets that the lists were requested (a new page load starts this way). Used by tests. */
export const resetReferenceRequested = () => {
  referenceRequested = false;
};

export const useBootstrapAppData = () => {
  const dispatch = useDispatch();
  const [getDataApiCall] = useGetDataMutation();
  const [getOrgInfo] = useGetOrganisationInfoMutation();
  const selectedOrg = useSelector((state) => state.auth?.user?.selectedOrg);
  const [displayZone, setDisplayZone] = useState(null);

  useEffect(() => {
    // The reference lists (currency, country codes) are the same for every organisation, so they
    // are requested once per page load: remounting a layout (app <-> settings) or switching
    // organisation does not refetch. They are deliberately not skipped just because the persisted
    // store already holds a copy, or a stale copy would never refresh. A failed request is retried
    // on the next mount.
    if (referenceRequested) return;
    referenceRequested = true;
    const getData = async () => {
      try {
        const res = await getDataApiCall().unwrap();
        dispatch(setData(res));
      } catch (err) {
        referenceRequested = false;
        console.error(err?.data?.message || err.error);
      }
    };
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedOrg) return;
    const loadTimeZone = async () => {
      try {
        const org = await getOrgInfo(selectedOrg).unwrap();
        const zone = org?.timeZoneIsSet ? org.timeZone : null;
        setDisplayTimeZone(zone);
        setDisplayZone(zone);
      } catch (err) {
        // Dates just stay in the browser's local time.
        console.error("Unable to load the organisation time zone:", err?.data?.message || err.error);
      }
    };
    loadTimeZone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  return displayZone;
};
